<template>
    <div>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <runtime-template-compiler v-if="!loading&&template" :template="template" :parent="parentComponent"/>
    </div>
</template>

<script>
import {callAPI, getNotification, getMaintenanceRecords} from '@/helper'
import LoadingProgressBar from './../LoadingProgressBar'
import {RuntimeTemplateCompiler} from "vue-runtime-template-compiler"
import moment from "moment";
import unitResolver from "../mixins/unitResolver";
import ValueBox from "./../ValueBox";
import data3Processor from "../mixins/data3Processor";

export default {
    props: {
        svg: {type: String, default: null},
        data3query: {type: Object, default: null},
        parameters: {type: Object, default: null},
        dateRange: {type: Object, default: null},
        includeIssues: {type: Boolean, default: false},
        includeMaintenance: {type: Boolean, default: false},
        equipment: {type:String, default: null},
    },
    components: {LoadingProgressBar,RuntimeTemplateCompiler,ValueBox},
    mixins:[unitResolver,data3Processor],
    name: 'SvgWidget',
    data: function () {
        return {
            loading: false,
            template: null,
            points: null,
            data: null,
            issues: [],
            maintenanceRecords: [],
            timeout: null,
            parentComponent: this,
        }
    },
    watch: {
        $props: {
            handler() {
                this.refresh()
            },
            deep: true,
            immediate: true,
        },
    },
    mounted() {
        let me=this
        if(this.dateRange&&this.svg&&this.svg!==""){
            let newTemplate="<div>"+this.svg+"</div>" //to avoid any vue borks due to multiple root elements in component
            let ids=newTemplate.match(/id="(.*?)"/g)
            if(ids&&ids.length>0){
                for (let id of ids){
                    if (id.startsWith('id="carnot--')){
                        let splitId=id.slice(0, -1).split("--")
                        if(!splitId[3]){
                            newTemplate=newTemplate.replaceAll(id,"v-text=\"points['"+splitId[1]+" - "+splitId[2]+"'] ? points['"+splitId[1]+" - "+splitId[2]+"'].value : ''\"")
                        } else if (splitId[3]==="show"){
                            newTemplate=newTemplate.replaceAll(id,"v-show=\"points['"+splitId[1]+" - "+splitId[2]+"']&&points['"+splitId[1]+" - "+splitId[2]+"'].value\"")
                        } else if (splitId[3]==="hide"){
                            newTemplate=newTemplate.replaceAll(id,"v-show=\"!points['"+splitId[1]+" - "+splitId[2]+"']||!points['"+splitId[1]+" - "+splitId[2]+"'].value\"")
                        }
                    }
                }
            }
            me.template=newTemplate
            me.refresh()
        }
    },
    methods: {
        debounce(callback, wait) {
            clearTimeout(this.timeout);
            this.timeout= setTimeout(() => callback(), wait);
        },
        refresh(){
            this.debounce(this.doRefresh,500);
        },
        async doRefresh() {
            this.loading = true
            if(this.includeIssues){
                let notifResult = await getNotification({
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                    date_range:[this.dateRange.start,moment(this.dateRange.end).format('YYYY-MM-DD')+" 23:59:59"],
                })
                this.issues = notifResult.result.filter(x=>x.topic!=="anomaly"&&!x.title.toLowerCase().startsWith("anomaly"))
            }
            if(this.includeMaintenance){
                let df = await getMaintenanceRecords({
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                    date_range:{start:this.dateRange.start,end:moment(this.dateRange.end).format('YYYY-MM-DD')+" 23:59:59"}
                })
                this.maintenanceRecords = df['result']
            }
            if(this.data3query){
                let payloadArray=JSON.parse(JSON.stringify(this.data3query['data-payload']))
                payloadArray=payloadArray.filter(x=>x.filters&&(x.filters.sensor_name||x.filters.equipment_name))
                for (let payload of payloadArray){
                    payload.parameters=this.parameters
                    this.processDateRange(payload, this.dateRange)
                    if(!payload.filters.facility_name&&this.$store.state.pointsListStore.pointsList.facility_name){
                        payload.filters.facility_name=this.$store.state.pointsListStore.pointsList.facility_name
                    }
                    if(this.equipment){
                        this.processEquipment(payload, this.equipment)
                    }
                    if(payload.onOnly){
                        if(!payload.pipeline){
                            payload.pipeline=[]
                        }
                        payload.pipeline.unshift(
                            {
                                "type": "filter",
                                "filter_op": "eq",
                                "filter_by": "* - on",
                                "filter_value": 1,
                            }
                        )
                    }
                }
                let results = await Promise.all(payloadArray.map(p=>callAPI('data3/',p)))
                let newPoints = {}
                let data = {}
                for (let result of results){
                    for (let seriesIndex in result.data) {
                        if (result.data.error){
                            continue
                        }
                        if (!data[seriesIndex]) {
                            data[seriesIndex] = result.data[seriesIndex]
                        }
                        if(seriesIndex==="timestamp"){
                            continue
                        }
                        let equipment_name = null
                        let sensor_name = null
                        let unit = null
                        if (seriesIndex.indexOf(" - ")>-1) {
                            equipment_name = seriesIndex.split(" - ")[0]
                            sensor_name = seriesIndex.split(" - ")[1]
                            unit=this.getUnit(sensor_name,equipment_name)
                            equipment_name = equipment_name.replaceAll("%", "_")
                            equipment_name = equipment_name.replaceAll("/", "_")
                            equipment_name = equipment_name.replaceAll("(", "_")
                            equipment_name = equipment_name.replaceAll(")", "_")
                            equipment_name = equipment_name.replaceAll(" ", "_")
                            sensor_name = sensor_name.replaceAll("%", "_")
                            sensor_name = sensor_name.replaceAll("/", "_")
                            sensor_name = sensor_name.replaceAll("(", "_")
                            sensor_name = sensor_name.replaceAll(")", "_")
                            sensor_name = sensor_name.replaceAll(" ", "_")
                        }
                        let value=result.data[seriesIndex].reduce((acc, curr) => curr!==null ? curr : acc)
                        newPoints[seriesIndex] = {
                            value: value,
                            unit: unit,
                            equipment_name: equipment_name,
                            sensor_name: sensor_name
                        }
                    }
                }
                this.points = newPoints
                this.data=data
            }
            let me = this
            setTimeout(()=>{me.loading=false},100)
        },
    },
}
</script>

<style scoped>

</style>
